import React, {useMemo} from 'react';

import {useViewAction} from '../../../state/views/hooks';
import * as PanelBankSectionConfigActions from '../../../state/views/panelBankSectionConfig/actions';
import {useSectionSettings} from '../../../state/views/panelBankSectionConfig/selectors';
import {type Ref as SectionRef} from '../../../state/views/panelBankSectionConfig/types';
import {useWorkspaceInheritedSettings} from './hooks/useWorkspaceInheritedSettings';
import {useSectionLinePlotSettings} from './runLinePlots/utils/getLinePlotSettings';
import {SectionSettingsContext} from './sectionSettingsContext';
import {useWorkspaceSettingsContext} from './WorkspaceSettingsContextProvider';

export {
  SectionSettingsContext,
  type SectionSettingsContextType,
  useSectionSettingsContext,
  useSectionSettingsContextMaybe,
} from './sectionSettingsContext';

type SectionSettingsProps = {
  sectionRef: SectionRef;
  children: React.ReactNode;
};

/**
 * This context provider is used in conjuction with WorkspaceSettingsContextProvider.
 * We use these two context providers to handle cascading settings logic.
 */
export const SectionSettingsContextProvider = ({
  sectionRef,
  children,
}: SectionSettingsProps) => {
  const workspaceSettingsContext = useWorkspaceSettingsContext();
  const sectionSettings = useSectionSettings(sectionRef);
  const sectionLinePlotSettings = useSectionLinePlotSettings(sectionSettings);
  const sectionMediaSettings = sectionSettings?.media;

  // This hook determines if workspace or section setting should be used.
  // We want UX to reflect if section level is using workspace level setting or not.
  const inheritedSettings = useWorkspaceInheritedSettings(
    workspaceSettingsContext,
    sectionLinePlotSettings
  );

  const updateLinePlotSectionSettings = useViewAction(
    sectionRef,
    PanelBankSectionConfigActions.updateLinePlotSectionSettings
  );

  const value = useMemo(
    () => ({
      inheritedSettings,
      linePlot: sectionLinePlotSettings,
      sectionRef,
      sectionSettings,
      updateLinePlotSectionSettings,
      sectionMediaSettings: sectionMediaSettings ?? {},
    }),
    [
      inheritedSettings,
      sectionLinePlotSettings,
      sectionRef,
      sectionSettings,
      updateLinePlotSectionSettings,
      sectionMediaSettings,
    ]
  );
  return (
    <SectionSettingsContext.Provider value={value}>
      {children}
    </SectionSettingsContext.Provider>
  );
};
